import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const base = `${API}/api/time-tracking`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ── Employees ─────────────────────────────────────────────────────────────────
const getEmployees  = (params = {}) => axios.get(`${base}/employees`, { ...auth(), params }).then(r => r.data.employees);
const createEmployee = (data)        => axios.post(`${base}/employees`, data, auth()).then(r => r.data.employee);
const updateEmployee = (id, data)    => axios.patch(`${base}/employees/${id}`, data, auth()).then(r => r.data.employee);
const terminateEmployee = (id)       => axios.post(`${base}/employees/${id}/terminate`, {}, auth()).then(r => r.data.employee);
const rehireEmployee  = (id)         => axios.post(`${base}/employees/${id}/rehire`, {}, auth()).then(r => r.data.employee);

// ── Terminal (no auth) ────────────────────────────────────────────────────────
const getTerminalStatus = (companyId, pin) =>
  axios.get(`${base}/terminal-status`, { params: { companyId, pin } }).then(r => r.data);
const punch = (companyId, pin) =>
  axios.post(`${base}/punch`, { companyId, pin }).then(r => r.data);

// ── Entries ───────────────────────────────────────────────────────────────────
const getEntries    = (params = {}) => axios.get(`${base}/entries`, { ...auth(), params }).then(r => r.data.entries);
const createEntry   = (data)        => axios.post(`${base}/entries`, data, auth()).then(r => r.data.entry);
const updateEntry   = (id, data)    => axios.patch(`${base}/entries/${id}`, data, auth()).then(r => r.data.entry);
const deleteEntry   = (id)          => axios.delete(`${base}/entries/${id}`, auth()).then(r => r.data);

// ── Summary & Reports ─────────────────────────────────────────────────────────
const getSummary      = (params = {}) => axios.get(`${base}/summary`, { ...auth(), params }).then(r => r.data.summary);
const getDailySummary = (params = {}) => axios.get(`${base}/summary/daily`, { ...auth(), params }).then(r => r.data.daily);
const getLaborSync    = (params = {}) => axios.get(`${base}/labor-sync`, { ...auth(), params }).then(r => r.data);

// ── CSV Export ────────────────────────────────────────────────────────────────
async function downloadCsv(url, filename) {
  const res = await axios.get(url, { ...auth(), responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
}
const exportTimecard  = (params) => downloadCsv(`${base}/export/timecard?${new URLSearchParams(params)}`,  `timecard-${params.from || 'all'}.csv`);
const exportPayPeriod = (params) => downloadCsv(`${base}/export/payperiod?${new URLSearchParams(params)}`, `payperiod-${params.from || 'all'}.csv`);

// ── Time Off ──────────────────────────────────────────────────────────────────
const getTimeOffSettings = ()         => axios.get(`${base}/time-off/settings`, auth()).then(r => r.data.settings);
const saveTimeOffSettings = (data)    => axios.put(`${base}/time-off/settings`, data, auth()).then(r => r.data.settings);

const getTimeOffPolicies  = ()        => axios.get(`${base}/time-off/policies`, auth()).then(r => r.data.policies);
const createTimeOffPolicy = (data)    => axios.post(`${base}/time-off/policies`, data, auth()).then(r => r.data.policy);
const updateTimeOffPolicy = (id, data) => axios.patch(`${base}/time-off/policies/${id}`, data, auth()).then(r => r.data.policy);
const deleteTimeOffPolicy = (id)      => axios.delete(`${base}/time-off/policies/${id}`, auth()).then(r => r.data);

const getTimeOffEntries   = (params = {}) => axios.get(`${base}/time-off/entries`, { ...auth(), params }).then(r => r.data.entries);
const createTimeOffEntry  = (data)    => axios.post(`${base}/time-off/entries`, data, auth()).then(r => r.data.entries);
const updateTimeOffEntry  = (id, data) => axios.patch(`${base}/time-off/entries/${id}`, data, auth()).then(r => r.data.entry);
const deleteTimeOffEntry  = (id)      => axios.delete(`${base}/time-off/entries/${id}`, auth()).then(r => r.data);

const getTimeOffBalances  = (params = {}) => axios.get(`${base}/time-off/balances`, { ...auth(), params }).then(r => r.data);

// ── Portal Access (admin) ─────────────────────────────────────────────────────
const enablePortalAccess  = (id, password) => axios.post(`${base}/employees/${id}/portal-enable`,  { password }, auth()).then(r => r.data);
const resetPortalPassword = (id, password) => axios.post(`${base}/employees/${id}/portal-reset`,   { password }, auth()).then(r => r.data);
const unlockPortalAccount = (id)           => axios.post(`${base}/employees/${id}/portal-unlock`,  {}, auth()).then(r => r.data);
const markProfileReviewed = (id)           => axios.post(`${base}/employees/${id}/profile-reviewed`, {}, auth()).then(r => r.data);

// ── Derogatory Marks ──────────────────────────────────────────────────────────
const getEmployeeMarks = (id, year) => axios.get(`${base}/employees/${id}/marks`, { ...auth(), params: { year } }).then(r => r.data);
const addEmployeeMark  = (id, data) => axios.post(`${base}/employees/${id}/marks`, data, auth()).then(r => r.data.mark);
const deleteMark       = (markId)   => axios.delete(`${base}/marks/${markId}`, auth()).then(r => r.data);

// ── Bonus Config ──────────────────────────────────────────────────────────────
const getBonusConfig  = (id, year)        => axios.get(`${base}/employees/${id}/bonus-config`, { ...auth(), params: { year } }).then(r => r.data);
const saveBonusConfig = (id, year, data)  => axios.put(`${base}/employees/${id}/bonus-config`, { year, ...data }, auth()).then(r => r.data);

// ── Point Policy ──────────────────────────────────────────────────────────────
const getPointPolicy  = ()       => axios.get(`${base}/point-policy`, auth()).then(r => r.data);
const savePointPolicy = (data)   => axios.put(`${base}/point-policy`, data, auth()).then(r => r.data);

// ── PTO Requests (admin) ──────────────────────────────────────────────────────
const getPtoRequests    = (params = {}) => axios.get(`${base}/pto-requests`, { ...auth(), params }).then(r => r.data.requests);
const approvePtoRequest = (id, adminNote) => axios.put(`${base}/pto-requests/${id}/approve`, { adminNote }, auth()).then(r => r.data);
const denyPtoRequest    = (id, adminNote) => axios.put(`${base}/pto-requests/${id}/deny`,    { adminNote }, auth()).then(r => r.data);

// ── Year-End Report ───────────────────────────────────────────────────────────
const getYearEndReport = (year) => axios.get(`${base}/year-end-report`, { ...auth(), params: { year } }).then(r => r.data);

// ── Bonus Metric Rules ────────────────────────────────────────────────────────
const getMetricRules       = (year)            => axios.get(`${base}/metric-rules`, { ...auth(), params: { year } }).then(r => r.data);
const createMetricRule     = (data)            => axios.post(`${base}/metric-rules`, data, auth()).then(r => r.data.rule);
const updateMetricRule     = (id, data)        => axios.put(`${base}/metric-rules/${id}`, data, auth()).then(r => r.data.rule);
const deleteMetricRule     = (id)              => axios.delete(`${base}/metric-rules/${id}`, auth()).then(r => r.data);
const getMetricProgress    = (year, granularity) => axios.get(`${base}/metric-rules/progress`, { ...auth(), params: { year, granularity } }).then(r => r.data);
const applyMetricDeductions = (year, overrides) => axios.post(`${base}/metric-rules/apply`, { year, overrides }, auth()).then(r => r.data);

const timeTrackingService = {
  getEmployees, createEmployee, updateEmployee, terminateEmployee, rehireEmployee,
  getTerminalStatus, punch,
  getEntries, createEntry, updateEntry, deleteEntry,
  getSummary, getDailySummary, getLaborSync,
  exportTimecard, exportPayPeriod,
  getTimeOffSettings, saveTimeOffSettings,
  getTimeOffPolicies, createTimeOffPolicy, updateTimeOffPolicy, deleteTimeOffPolicy,
  getTimeOffEntries, createTimeOffEntry, updateTimeOffEntry, deleteTimeOffEntry,
  getTimeOffBalances,
  enablePortalAccess, resetPortalPassword, unlockPortalAccount, markProfileReviewed,
  getEmployeeMarks, addEmployeeMark, deleteMark,
  getBonusConfig, saveBonusConfig,
  getPointPolicy, savePointPolicy,
  getPtoRequests, approvePtoRequest, denyPtoRequest,
  getYearEndReport,
  getMetricRules, createMetricRule, updateMetricRule, deleteMetricRule,
  getMetricProgress, applyMetricDeductions,
  getEmployeeEvents: (id) => axios.get(`${base}/employees/${id}/events`, auth()).then(r => r.data.events),
};

export default timeTrackingService;
